import React, { useState, useMemo, useEffect } from 'react';
import { nurseAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';
import { toast } from 'react-toastify';

const Calendar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [calendarView, setCalendarView] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSessionsForModal, setSelectedSessionsForModal] = useState(null);

  const closeModal = () => setSelectedSessionsForModal(null);

  // Calculate current week dates dynamically based on selectedDate
  const weekDates = useMemo(() => {
    const startOfWeek = new Date(selectedDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [selectedDate]);

  // Update time slot generation to show 10-minute intervals from 10 AM to 6 PM
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 10; hour <= 18; hour++) { // Start at 10 AM, end at 6 PM (18:00)
      const hourStr = hour > 12 ? (hour - 12) : hour;
      const period = hour >= 12 ? 'PM' : 'AM';
      
      // Generate slots for every 10 minutes
      for (let minute = 0; minute < 60; minute += 20) {
        // Don't add slots after 6:00 PM
        if (hour === 18 && minute > 0) continue;
        
        const minuteStr = minute.toString().padStart(2, '0');
        slots.push(`${hourStr}:${minuteStr} ${period}`);
      }
    }
    return slots;
  };

  // Update time comparison function
  const isSameTime = (time1, time2) => {
    if (!time1 || !time2) return false;
    
    // Normalize times by removing extra spaces and converting to uppercase
    const normalizeTime = (time) => {
      return time.replace(/\s+/g, ' ').trim().toUpperCase();
    };

    return normalizeTime(time1) === normalizeTime(time2);
  };

  // Update appointment lookup for time slot
  const getAppointmentForTimeSlot = (date, time) => {
    const session = sessions.find(session => {
      const sessionDate = new Date(session.date).toDateString();
      const slotDate = date.toDateString();
      const timeMatches = isSameTime(session.startTime, time);
      
      console.log('Comparing session:', {
        sessionDate,
        slotDate,
        sessionTime: session.startTime,
        slotTime: time,
        matches: sessionDate === slotDate && timeMatches
      });

      return sessionDate === slotDate && timeMatches;
    });

    if (session) {
      console.log('Found session for slot:', { date: date.toDateString(), time, session });
    }

    return session;
  };

  // Update session fetching
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        const user = tokenManager.getUser();
        const nurseId = user?._id || user?.id;

        if (!nurseId) {
          toast.error('User ID not found');
          return;
        }

        let dateFrom, dateTo;
        if (calendarView === 'week') {
          dateFrom = weekDates[0].toISOString().split('T')[0];
          dateTo = weekDates[6].toISOString().split('T')[0];
        } else {
          // For month view, include the entire month plus adjacent days
          const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
          const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
          
          // Add buffer days to handle timezone differences
          firstDay.setDate(firstDay.getDate() - 7);
          lastDay.setDate(lastDay.getDate() + 7);
          
          dateFrom = firstDay.toISOString().split('T')[0];
          dateTo = lastDay.toISOString().split('T')[0];
        }

        const response = await nurseAPI.getNurseSchedule(nurseId, {
          dateFrom,
          dateTo,
          status: 'all status'
        });

        if (response?.sessions) {
          const formattedSessions = response.sessions.map(session => {
            // Convert session date to local timezone
            const sessionDate = new Date(session.date);
            const localDate = new Date(sessionDate.getTime() - sessionDate.getTimezoneOffset() * 60000);
            
            return {
              id: session._id,
              time: session.startTime,
              duration: session.durationMinutes || 10,
              date: localDate,
              patient: session.patientName || session.patientId?.name || 'Unknown Patient',
              email: session.patientId?.email,
              type: session.sessionType === 'monthly_checkin' ? 'Monthly Check-in' : session.sessionType,
              status: session.status,
              patientId: session.patientId?._id,
              sessionMode: session.sessionMode,
              monthlyAssessmentCompleted: session.monthlyAssessmentCompleted,
              startTime: session.startTime,
              endTime: session.endTime
            };
          });

          setSessions(formattedSessions);
        }
      } catch (error) {
        console.error('Error fetching nurse sessions:', error);
        toast.error('Failed to fetch sessions');
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [selectedDate, calendarView, weekDates]);

  // Get days for month view
  const getMonthDates = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    
    // Get first and last day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the first Monday before or on the first day of the month
    const startDate = new Date(firstDay);
    const startDay = firstDay.getDay();
    startDate.setDate(firstDay.getDate() - (startDay === 0 ? 6 : startDay - 1));
    
    // Get the last Sunday after or on the last day of the month
    const endDate = new Date(lastDay);
    const endDay = lastDay.getDay();
    if (endDay !== 0) {
      endDate.setDate(lastDay.getDate() + (7 - endDay));
    }

    const dates = [];
    const currentDate = new Date(startDate);
    
    // Generate array of dates
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === selectedDate.getMonth();
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  // Update the getAppointmentsForDate function to handle timezone correctly
  const getAppointmentsForDate = (date) => {
    return sessions.filter(session => {
      const sessionDate = new Date(session.date);
      const compareDate = new Date(date);
      
      // Compare dates without time component
      return (
        sessionDate.getFullYear() === compareDate.getFullYear() &&
        sessionDate.getMonth() === compareDate.getMonth() &&
        sessionDate.getDate() === compareDate.getDate()
      );
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleSessionClick = (session) => {
    // You can implement session details view or edit functionality here
    console.log('Session clicked:', session);
  };

  const timeSlots = generateTimeSlots();
  const monthDates = getMonthDates();

  // Update the week view session rendering
  const renderWeekViewSession = (session) => {
    if (!session) return null;

    return (
      <div className={`p-2 rounded text-xs font-medium border ${getStatusColor(session.status)}`}>
        <div className="font-semibold truncate">{session.patient}</div>
        <div className="text-gray-600 mt-1 truncate">{session.type}</div>
        <div className="text-gray-500 mt-1 truncate">{session.sessionMode}</div>
        {/* {!session.monthlyAssessmentCompleted && (
          <div className="text-red-500 mt-1 text-xs">Assessment pending</div>
        )} */}
      </div>
    );
  };

  // Update the month view session rendering
  const renderMonthViewSessions = (sessions) => {
    const maxVisible = 2;
    const safeSessions = Array.isArray(sessions) ? sessions : [];
    const visibleSessions = safeSessions.slice(0, maxVisible);
    const extraSessions = safeSessions.length - maxVisible;

    return (
      <div>
        {visibleSessions.map((session) => (
          <div
            key={session.id}
            className={`text-xs p-2 mb-1 rounded-lg font-medium border-l-4 ${getStatusColor(session.status)}`}
            onClick={() => handleSessionClick(session)}
          >
            <div className="font-semibold text-xs">{session.time}</div>
            <div className="font-medium truncate">{session.patient}</div>
            <div className="text-xs opacity-75 truncate mt-1">{session.type}</div>
            <div className="text-xs opacity-75 truncate">{session.sessionMode}</div>
          </div>
        ))}

        {extraSessions > 0 && (
          <button
            onClick={() => setSelectedSessionsForModal(safeSessions)}
            className="text-blue-600 text-xs mt-2 underline"
          >
            View all ({safeSessions.length})
          </button>
        )}
      </div>
    );
  };



  // Get sessions for a time range
  const getSessionsInTimeRange = (date, startSlot, nextSlot) => {
    return sessions.filter(session => {
      const sessionDate = new Date(session.date).toDateString();
      const slotDate = date.toDateString();
      if (sessionDate !== slotDate) return false;

      // Convert all times to minutes for comparison
      const getMinutes = (timeStr) => {
        const [time, period] = timeStr.split(' ');
        const [hours, minutes] = time.split(':').map(Number);
        let totalMinutes = hours * 60 + minutes;
        if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
        if (period === 'AM' && hours === 12) totalMinutes = minutes;
        return totalMinutes;
      };

      const sessionMinutes = getMinutes(session.startTime);
      const slotStartMinutes = getMinutes(startSlot);
      const slotEndMinutes = nextSlot ? getMinutes(nextSlot) : slotStartMinutes + 30;

      return sessionMinutes >= slotStartMinutes && sessionMinutes < slotEndMinutes;
    });
  };

  // Update week view cell rendering
  const renderWeekViewCell = (date, time, index, totalSlots) => {
    const nextSlot = index < totalSlots - 1 ? timeSlots[index + 1] : null;
    const sessionsInSlot = getSessionsInTimeRange(date, time, nextSlot);

    const maxVisible = 2;
    const visibleSessions = sessionsInSlot.slice(0, maxVisible);
    const extraSessions = sessionsInSlot.length - maxVisible;

    return (
      <div
        key={`${date.toDateString()}-${time}`}
        className={`bg-white p-2 min-h-[100px] hover:bg-gray-50 transition-colors cursor-pointer border-r border-gray-200`}
      >
        {visibleSessions.map((session) => (
          <div
            key={session.id}
            className={`p-2 mb-1 rounded text-xs font-medium border ${getStatusColor(session.status)}`}
            onClick={() => handleSessionClick(session)}
          >
            <div className="font-semibold text-xs">{session.startTime}</div>
            <div className="font-semibold truncate">{session.patient}</div>
            <div className="text-gray-600 mt-1 truncate">{session.type}</div>
            <div className="text-gray-500 mt-1 truncate">{session.sessionMode}</div>
          </div>
        ))}

        {extraSessions > 0 && (
          <button
            onClick={() => setSelectedSessionsForModal(sessionsInSlot)}
            className="text-blue-600 text-xs underline mt-1"
          >
             View all ({sessionsInSlot.length})
          </button>
        )}
      </div>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Calendar</h1>
          <p className="text-gray-600">Manage your appointments and schedule</p>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="relative flex items-center justify-between p-4 border-b border-gray-200">
          {/* Left: Week/Month Toggle */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg z-10">
            <button
              onClick={() => setCalendarView('week')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${calendarView === 'week'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Week
            </button>
            <button
              onClick={() => setCalendarView('month')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${calendarView === 'month'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Month
            </button>
          </div>

          {/* Center: Absolutely Centered Date Navigation */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateDate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
                {calendarView === 'week'
                  ? `${formatDate(weekDates[0])} - ${formatDate(weekDates[6])}`
                  : formatMonthYear(selectedDate)}
              </div>

              <button
                onClick={() => navigateDate(1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Right: (Optional) */}
          <div className="z-10">
            {/* Right-side buttons or filters if needed */}
          </div>
        </div>

        {/* Calendar Content */}
        <div className="p-6">
          {calendarView === 'week' ? (
            /* Week View */
            <div className="grid grid-cols-8 gap-px bg-gray-200 rounded-lg overflow-hidden">
              {/* Time column header */}
              <div className="bg-gray-50 p-3 text-center">
                <span className="text-xs font-medium text-gray-500">Time</span>
              </div>
              
              {/* Day headers */}
              {weekDates.map((date, index) => (
                <div
                  key={index}
                  className={`bg-gray-50 p-3 text-center ${
                    isToday(date) ? 'bg-blue-50 border-b-2 border-blue-500' : ''
                  }`}
                >
                  <div className="text-xs font-medium text-gray-500">
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className={`text-sm font-semibold mt-1 ${
                    isToday(date) ? 'text-blue-600' : 'text-gray-900'
                  }`}>
                    {date.getDate()}
                  </div>
                </div>
              ))}

              {/* Time slots */}
              {timeSlots.map((time, index) => (
                <React.Fragment key={time}>
                  {/* Time label */}
                  <div className="bg-white p-3 flex items-start justify-center border-r border-gray-200">
                    <span className="text-xs text-gray-500">{time}</span>
                  </div>
                  
                  {/* Day cells */}
                  {weekDates.map((date) => renderWeekViewCell(date, time, index, timeSlots.length))}
                </React.Fragment>
              ))}
            </div>
          ) : (
            /* Month View */
            <div>
              {/* Month grid header */}
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-t-lg overflow-hidden">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                  <div key={day} className="bg-gray-50 p-3 text-center">
                    <span className="text-xs font-medium text-gray-500">{day}</span>
                  </div>
                ))}
              </div>

              {/* Month grid */}
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-b-lg overflow-hidden">
                {Array.from({ length: Math.ceil(monthDates.length / 7) }, (_, weekIndex) =>
                  monthDates.slice(weekIndex * 7, (weekIndex + 1) * 7).map((date, dayIndex) => {
                    const daySessions = getAppointmentsForDate(date);
                    
                    return (
                      <div
                        key={`${weekIndex}-${dayIndex}`}
                        className={`bg-white p-3 min-h-[140px] hover:bg-gray-50 transition-colors cursor-pointer ${
                          !isCurrentMonth(date) ? 'bg-gray-50 text-gray-400' : ''
                        } ${
                          isToday(date) ? 'bg-blue-50 border-2 border-blue-500' : ''
                        }`}
                      >
                        <div className={`text-sm font-semibold mb-3 ${
                          isToday(date) ? 'text-blue-600' : 
                          isCurrentMonth(date) ? 'text-gray-900' : 'text-gray-400'
                        }`}>
                          {date.getDate()}
                        </div>
                        
                        {/* Sessions for this date */}
                        <div className="space-y-1">
                          {renderMonthViewSessions(daySessions)}
                        </div>

                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    {
    selectedSessionsForModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">All Sessions</h2>
            <button onClick={closeModal} className="text-gray-500 hover:text-black">&times;</button>
          </div>
          {selectedSessionsForModal.map((session) => (
            <div
              key={session.id}
              className={`text-sm p-2 rounded-lg border-l-4 mb-2 ${getStatusColor(session.status)}`}
            >
              <div className="font-semibold">{session.time}</div>
              <div>{session.patient}</div>
              <div className="opacity-75">{session.type}</div>
              <div className="opacity-75">{session.sessionMode}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }
</>
  );
};

export default Calendar;